import { FC } from 'react';
import { TabbedFormProps, useNotify, useRecordContext, useRefresh, useUpdate } from 'react-admin';

import CraftEdit from 'src/components/CraftEdit';
import CraftTabbedForm from 'src/components/CraftTabbedForm';
import LoadingOverlay from 'src/components/LoadingOverlay';
import ArchivedOrDeletedNotification from 'src/components/ArchivedOrDeletedNotification';
import DetailsTab from './formTabs/Details';
import EditAside from './EditAside';
import { ExternalClientRecord } from 'src/types';
import { editIdentityExternalClientSchema, getFormValidator } from './validation';

const validateForm = getFormValidator();

const transformValues = (rawValues: Partial<ExternalClientRecord>) => {
  const values = editIdentityExternalClientSchema.parse(rawValues);
  return {
    ...values,
    updated_at: 'now()',
  };
};

const CustomTabbedForm = ({ children, ...rest }: TabbedFormProps) => {
  const record = useRecordContext<ExternalClientRecord>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [softDeleteExternalClient, { isLoading: softDeleteLoading }] = useUpdate();

  const handleDelete = async () => {
    if (!record) return;

    try {
      await softDeleteExternalClient(
        'identity_external_clients',
        { id: record.id, data: { deleted_at: 'now()' } },
        { returnPromise: true },
      );
      notify('The External Client has been successfully deleted', { type: 'success' });
      refresh();
    } catch (e) {
      console.error(e);
      notify('There was a problem deleting the External Client. Please try again.', { type: 'error' });
    }
  };

  return (
    <>
      <CraftTabbedForm
        formType="edit"
        deleteOptions={{
          deletePermission: 'externalClient:delete',
          onConfirmDelete: handleDelete,
          dialogTitle: `Delete External Client "${record?.name}"`,
        }}
        validate={validateForm}
        shouldUnregister
        {...rest}
      >
        {children}
      </CraftTabbedForm>

      <LoadingOverlay open={softDeleteLoading} />
      <ArchivedOrDeletedNotification record={record} notificationText="This External Client is DELETED" />
    </>
  );
};

const ExternalClientsEdit: FC = () => (
  <CraftEdit
    header="Edit External Client"
    aside={<EditAside />}
    redirect={false}
    transform={transformValues}
    mutationMode="optimistic"
  >
    <CustomTabbedForm>
      <DetailsTab mode="edit" />
    </CustomTabbedForm>
  </CraftEdit>
);

export default ExternalClientsEdit;
